import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import OneSignal, { LogLevel, type NotificationClickEvent } from '@onesignal/capacitor-plugin';
import { useAuth } from '@/contexts/AuthContext';
import { getNotificationPath } from '@/pages/app-notifications/deepLink';
import { markNotificationRead } from '@/pages/app-notifications/AppNotifications.api';

const getString = (value: unknown) => (typeof value === 'string' ? value : null);

export const usePushNotifications = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
    if (!appId) {
      if (import.meta.env.DEV) console.warn('[OneSignal] VITE_ONESIGNAL_APP_ID not set');
      return;
    }

    const handleClick = (event: NotificationClickEvent) => {
      const data = (event.notification.additionalData ?? {}) as Record<string, unknown>;
      const deepLink = getString(data.deepLink);
      const notificationId = getString(data.id);

      if (notificationId) {
        void markNotificationRead(notificationId)
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: ['notifications'] });
            void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
          })
          .catch(() => undefined);
      }

      const path = getNotificationPath(deepLink);
      if (path) navigateRef.current(path);
    };

    if (import.meta.env.DEV) {
      OneSignal.Debug.setLogLevel(LogLevel.Verbose);
    }
    void OneSignal.initialize(appId).catch((error) => {
      console.error('OneSignal initialization failed:', error);
    });
    OneSignal.Notifications.addEventListener('click', handleClick);

    return () => {
      try {
        OneSignal.Notifications.removeEventListener('click', handleClick);
      } catch {
        // Ignore errors during cleanup, e.g. if OneSignal wasn't initialized
      }
    };
  }, [queryClient]);
};

export const setOneSignalExternalUserId = (userId: string): void => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  void OneSignal.login(userId).catch((error) => {
    console.error('Failed to set OneSignal external user ID:', error);
  });
};

export const useRequestPushPermissionIfNeeded = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user) {
      return;
    }

    const requestIfNeeded = async () => {
      try {
        const hasPermission = await OneSignal.Notifications.hasPermission();
        if (!hasPermission) {
          await OneSignal.Notifications.requestPermission(false);
        }
      } catch (error) {
        console.error('Failed to request push notification permission:', error);
      }
    };

    requestIfNeeded();
  }, [user]);
};

export const PushNotifications = () => {
  usePushNotifications();
  useRequestPushPermissionIfNeeded();
  return null;
};
